/**
 * SxS Network — Garage Data Layer (v3 — ride profile + metafield fitment)
 * -----------------------------------------------------------------------
 * localStorage-based persistence + STRICT fitment matching.
 *
 * v3 CHANGES vs v2 (2 Oct 2026):
 *   - ride PROFILE: { use, styles[], winter, trails[], other_trail, maps[] (v6, 3 Oct 2026), interests[], notes }
 *     getProfile / setProfile / rideLine (one-line summary for forms)
 *   - machine "What's on my machine" fields: cage, tube, tires, lift, roof, doors
 *   - matchesDisplay(): matcher for the custom.fitment_display metafield
 *     ("Make Model : 2017+" lines) — ~3,400 SuperATV products carry it
 *   - 'universal' match from the store tag Universal
 *   - state version 2; v1 states are upgraded in place
 *
 * v2 (kept): matcher REQUIRES populated fits_makes AND fits_models to claim a
 * match; missing data returns { dataIncomplete: true }.
 */

(function() {
  if (window.SxSNGarage && window.SxSNGarage.version >= 3) return; // loaded once site-wide from meta-tags.liquid; page/product includes are no-ops
  var STORAGE_KEY = 'sxsn_garage_v1';
  var YEAR_CAP = 2027; // "2017+" means 2017..YEAR_CAP (same cap as the fitment import)
  var listeners = [];

  function emptyProfile() {
    return { use: null, styles: [], winter: null, trails: [], other_trail: null, maps: [], interests: [], notes: null, updated_at: null };
  }

  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { machines: [], primaryId: null, profile: emptyProfile(), version: 2 };
      var parsed = JSON.parse(raw);
      if (!parsed.machines) parsed.machines = [];
      if (!parsed.profile) parsed.profile = emptyProfile();
      return parsed;
    } catch (e) {
      console.warn('[Garage] Read error:', e);
      return { machines: [], primaryId: null, profile: emptyProfile(), version: 2 };
    }
  }

  function write(state) {
    try {
      state.version = 2;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      listeners.forEach(function(cb) {
        try { cb(state); } catch (e) { console.warn('[Garage] Listener error:', e); }
      });
      try {
        window.dispatchEvent(new CustomEvent('sxsn:garage-changed', { detail: state }));
      } catch (e) { /* IE fallback */ }
    } catch (e) {
      console.warn('[Garage] Write error:', e);
    }
  }

  function generateId() {
    return 'm_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
  }

  function norm(s) {
    return (s || '').toString().toLowerCase().replace(/[–—]/g, '-').replace(/\s+/g, ' ').trim();
  }

  /**
   * Parse custom.fitment_display text into [{make, model, years:[...]}, ...]
   * Lines look like "Polaris RZR XP Turbo : 2017-2021", "Honda Pioneer 700 : 2017+",
   * "Can-Am Maverick Trail 700 : 2022+". Lines without " : " (notes, OEM part
   * numbers) are ignored. Lines under a "Does not fit" header are excluded.
   */
  function parseDisplay(text) {
    var out = [];
    if (!text) return out;
    var excluding = false;
    text.split(/\r?\n/).forEach(function(line) {
      var l = line.trim();
      if (!l) return;
      if (/does not fit/i.test(l)) { excluding = true; return; }
      if (excluding) return;
      var parts = l.split(/\s*:\s*/);
      if (parts.length < 2) return;
      var vehicle = parts[0].trim();
      var yearsTxt = parts.slice(1).join(':').trim();
      var years = [];
      var m;
      if ((m = yearsTxt.match(/^(\d{4})\s*-\s*(\d{4})/))) {
        for (var y = +m[1]; y <= +m[2]; y++) years.push(y);
      } else if ((m = yearsTxt.match(/^(\d{4})\s*\+/))) {
        for (var y2 = +m[1]; y2 <= YEAR_CAP; y2++) years.push(y2);
      } else {
        var found = yearsTxt.match(/\d{4}/g);
        if (found) found.forEach(function(yy) { years.push(+yy); });
      }
      var words = vehicle.split(' ');
      var make = words.shift() || '';
      // two-word makes
      if (/^arctic$/i.test(make) && words.length) make += ' ' + words.shift();
      if (/^john$/i.test(make) && words.length) make += ' ' + words.shift();
      out.push({ make: make, model: words.join(' '), years: years, raw: l });
    });
    return out;
  }

  function modelMatches(lineModel, garageModel) {
    var a = norm(lineModel), b = norm(garageModel);
    if (!a || !b) return false;
    if (a === b) return true;
    // product line is more specific than the garage model: "maverick trail 700" vs "maverick trail"
    if (a.indexOf(b + ' ') === 0) return true;
    // common aliases
    var alias = { 'maverick x3 max': 'maverick x3 max', 'maverick x3 maxx': 'maverick x3 max', 'defender max': 'defender max' };
    if (alias[a] && alias[a] === alias[b]) return true;
    return false;
  }

  function makeMatches(lineMake, garageMake) {
    var a = norm(lineMake).replace(/-/g, ''), b = norm(garageMake).replace(/-/g, '');
    if (a === b) return true;
    if ((a === 'textron' || a === 'arctic cat') && (b === 'textron / arctic cat' || b === 'arctic cat' || b === 'textron')) return true;
    if ((b === 'textron / arctic cat') && (a === 'arctic cat' || a === 'textron')) return true;
    return false;
  }

  window.SxSNGarage = {
    version: 3,
    list: function() { return read().machines; },
    get: function(id) { return read().machines.find(function(m) { return m.id === id; }) || null; },

    add: function(machine) {
      if (!machine || !machine.make || !machine.model) {
        throw new Error('Garage.add requires at least make and model');
      }
      var state = read();
      var newMachine = {
        id: generateId(),
        make: machine.make,
        model: machine.model,
        year: machine.year || null,
        trim: machine.trim || null,
        nickname: machine.nickname || null,
        photo_url: machine.photo_url || null,
        notes: machine.notes || null,
        // "What's on my machine" (v3)
        cage: machine.cage || null,        // 'stock' | 'aftermarket'
        tube: machine.tube || null,        // e.g. '1.75"' | '2"'
        tires: machine.tires || null,      // e.g. '32"'
        lift: machine.lift || null,        // 'none' | 'lift' | 'portals'
        roof: machine.roof || null,        // 'none' | 'poly' | 'aluminum'
        doors: machine.doors || null,      // 'none' | 'half' | 'full'
        added_at: Date.now()
      };
      state.machines.push(newMachine);
      if (!state.primaryId) state.primaryId = newMachine.id;
      write(state);
      return newMachine;
    },

    update: function(id, updates) {
      var state = read();
      var idx = state.machines.findIndex(function(m) { return m.id === id; });
      if (idx === -1) return null;
      state.machines[idx] = Object.assign({}, state.machines[idx], updates, { id: id });
      write(state);
      return state.machines[idx];
    },

    remove: function(id) {
      var state = read();
      var idx = state.machines.findIndex(function(m) { return m.id === id; });
      if (idx === -1) return false;
      state.machines.splice(idx, 1);
      if (state.primaryId === id) {
        state.primaryId = state.machines.length > 0 ? state.machines[0].id : null;
      }
      write(state);
      return true;
    },

    setPrimary: function(id) {
      var state = read();
      var exists = state.machines.some(function(m) { return m.id === id; });
      if (!exists) return false;
      state.primaryId = id;
      write(state);
      return true;
    },

    getPrimary: function() {
      var state = read();
      if (!state.primaryId) return null;
      return state.machines.find(function(m) { return m.id === state.primaryId; }) || null;
    },

    clear: function(skipConfirm) {
      if (!skipConfirm && !window.confirm('Remove all machines from your garage?')) return false;
      write({ machines: [], primaryId: null, profile: emptyProfile(), version: 2 });
      return true;
    },

    count: function() { return read().machines.length; },

    // ==========  RIDE PROFILE (v3)  ==========
    getProfile: function() { return read().profile || emptyProfile(); },

    setProfile: function(updates) {
      var state = read();
      state.profile = Object.assign({}, state.profile || emptyProfile(), updates, { updated_at: Date.now() });
      write(state);
      return state.profile;
    },

    hasProfile: function() {
      var p = read().profile || emptyProfile();
      return !!(p.use || (p.styles && p.styles.length) || (p.trails && p.trails.length) || (p.maps && p.maps.length) || (p.interests && p.interests.length) || p.winter);
    },

    /** One line a human can read — used to prefill Build Concierge / Service Quote / product questions. */
    rideLine: function() {
      var p = read().profile || emptyProfile();
      var bits = [];
      if (p.use) bits.push(p.use === 'both' ? 'Play + work' : (p.use.charAt(0).toUpperCase() + p.use.slice(1)));
      if (p.styles && p.styles.length) bits.push(p.styles.join(', '));
      if (p.winter) bits.push('rides in winter');
      var trails = (p.trails || []).slice();
      if (p.other_trail) trails.push(p.other_trail);
      if (trails.length) bits.push('rides at ' + trails.join(', '));
      if (p.maps && p.maps.length) bits.push('navigates with ' + p.maps.join(', '));
      if (p.interests && p.interests.length) bits.push('into ' + p.interests.join(', '));
      return bits.length ? 'I ride: ' + bits.join(' · ') : '';
    },

    /** One line describing a machine incl. what's on it. */
    machineLine: function(m) {
      if (!m) return '';
      var label = this.formatLabel(m);
      var mods = [];
      if (m.tires) mods.push(m.tires + ' tires');
      if (m.cage === 'aftermarket') mods.push('aftermarket cage' + (m.tube ? ' (' + m.tube + ' tube)' : ''));
      if (m.cage === 'stock') mods.push('stock cage');
      if (m.lift === 'lift') mods.push('lift kit');
      if (m.lift === 'portals') mods.push('portals');
      if (m.roof && m.roof !== 'none') mods.push(m.roof + ' roof');
      if (m.doors && m.doors !== 'none') mods.push(m.doors + ' doors');
      if (m.notes) mods.push(m.notes);
      return label + (mods.length ? ' — ' + mods.join(', ') : '');
    },

    // ==========  FITMENT  ==========
    parseDisplay: parseDisplay,

    /**
     * Match the garage against a product's custom.fitment_display text.
     * Returns the same shape as matches(): [{machine, matchType, line}] or [{dataIncomplete:true}].
     *   'universal'  → product tagged Universal
     *   'exact'      → make + model + (year in the line's year range)
     *   'model'      → make + model match, product line has no years or machine has no year
     * Machines that don't match are simply absent (wrong year = no match, not 'model').
     */
    matchesDisplay: function(displayText, isUniversal) {
      var machines = read().machines;
      if (machines.length === 0) return [];
      if (isUniversal) return machines.map(function(m) { return { machine: m, matchType: 'universal' }; });
      var lines = parseDisplay(displayText);
      if (lines.length === 0) return [{ dataIncomplete: true }];
      var results = [];
      machines.forEach(function(m) {
        var best = null;
        lines.forEach(function(ln) {
          if (!makeMatches(ln.make, m.make)) return;
          if (!modelMatches(ln.model, m.model)) return;
          if (ln.years.length && m.year) {
            if (ln.years.indexOf(+m.year) === -1) return; // wrong year for this line
            best = best && best.matchType === 'exact' ? best : { machine: m, matchType: 'exact', line: ln.raw };
          } else if (!best) {
            best = { machine: m, matchType: 'model', line: ln.raw };
          }
        });
        if (best) results.push(best);
      });
      return results;
    },

    /**
     * STRICT fitment matcher (v2, tag/metafield lists) — kept for compatibility.
     */
    matches: function(productFitment) {
      var machines = read().machines;
      if (!productFitment || machines.length === 0) return [];

      if (productFitment.type === 'universal' || productFitment.universal === true) {
        return machines.map(function(m) { return { machine: m, matchType: 'universal' }; });
      }

      var fitsMakes = (productFitment.fits_makes || []).map(norm).filter(Boolean);
      var fitsModels = (productFitment.fits_models || []).map(norm).filter(Boolean);
      var fitsYears = productFitment.fits_years || [];
      var fitsTrims = (productFitment.fits_trims || []).map(norm).filter(Boolean);

      if (fitsMakes.length === 0 || fitsModels.length === 0) {
        return [{ dataIncomplete: true }];
      }

      var results = [];
      machines.forEach(function(m) {
        var make = norm(m.make);
        var model = norm(m.model);
        var trim = norm(m.trim);
        if (fitsMakes.indexOf(make) === -1) return;
        if (fitsModels.indexOf(model) === -1) return;
        if (fitsYears.length > 0) {
          if (!m.year || fitsYears.indexOf(m.year) === -1) return;
        }
        if (fitsTrims.length > 0) {
          if (trim && fitsTrims.indexOf(trim) !== -1) {
            results.push({ machine: m, matchType: 'trim_exact' });
          } else {
            return;
          }
        } else if (fitsYears.length > 0 && m.year) {
          results.push({ machine: m, matchType: 'exact' });
        } else {
          results.push({ machine: m, matchType: 'model' });
        }
      });
      return results;
    },

    onChange: function(callback) {
      if (typeof callback === 'function') listeners.push(callback);
      return function unsubscribe() {
        var idx = listeners.indexOf(callback);
        if (idx !== -1) listeners.splice(idx, 1);
      };
    },

    formatLabel: function(machine) {
      if (!machine) return '';
      var parts = [];
      if (machine.year) parts.push(machine.year);
      if (machine.make) parts.push(machine.make);
      if (machine.model) parts.push(machine.model);
      if (machine.trim) parts.push(machine.trim);
      var label = parts.join(' ');
      if (machine.nickname) label = '"' + machine.nickname + '" — ' + label;
      return label;
    }
  };

  window.addEventListener('storage', function(e) {
    if (e.key === STORAGE_KEY) {
      var state = read();
      listeners.forEach(function(cb) {
        try { cb(state); } catch (err) { /* swallow */ }
      });
    }
  });
})();
